/**
 * Screen building blocks composed from @qm/ui. Brand tokens only (kit.css); logical properties for RTL.
 * Candidates for promotion to @qm/ui (integration request in docs/status/ui-agency-admin.md).
 */
import { cloneElement, isValidElement, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Button, Heading, MicroLabel, Panel, Text } from '@qm/ui';
import type { Translate } from '@qm/i18n';
import { useSurface } from './context';
import { errorInfo } from './http';
import type { Resource } from './resource';

/** Editorial screen: mono eyebrow, oversized tight title (optional serif `pivot`), fine rule, actions. */
export function Screen({ title, subtitle, actions, children, testId, eyebrow, pivot }: { title: string; subtitle?: string; actions?: ReactNode; children: ReactNode; testId?: string; eyebrow?: string; pivot?: boolean | ReactNode }) {
  const { dir, locale } = useSurface();
  return (
    <section className="qx-screen" dir={dir} lang={locale} data-testid={testId}>
      <header className="qx-screen__head">
        <div className="qx-screen__title">
          {eyebrow ? <MicroLabel>{eyebrow}</MicroLabel> : null}
          <Heading level={1} pivot={pivot}>{title}</Heading>
          {subtitle ? <Text tone="muted">{subtitle}</Text> : null}
        </div>
        {actions ? <div className="qx-screen__actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

export type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'accent';
export function Badge({ tone = 'neutral', children, testId }: { tone?: Tone; children: ReactNode; testId?: string }) {
  return <span className="qx-badge" data-tone={tone} data-testid={testId}>{children}</span>;
}

export interface TabDef { id: string; label: string; count?: number | null }
/**
 * WAI-ARIA tabs: roving tabindex (only the selected tab is in the Tab order), Arrow keys move and select (mirrored in
 * RTL), Home/End jump. When children are given they render as the tabpanel, labelled by and controlled from the tab.
 */
export function Tabs({ tabs, active, onSelect, label, children }: { tabs: readonly TabDef[]; active: string; onSelect: (id: string) => void; label: string; children?: ReactNode }) {
  const { dir } = useSurface();
  const base = useId();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelId = `${base}-panel`;
  const move = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const fwd = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    const next = e.key === fwd ? (index === last ? 0 : index + 1) : e.key === back ? (index === 0 ? last : index - 1) : e.key === 'Home' ? 0 : e.key === 'End' ? last : null;
    if (next === null) return;
    e.preventDefault();
    const tb = tabs[next]!;
    onSelect(tb.id);
    refs.current[tb.id]?.focus();
  };
  return (
    <>
      <div className="qx-tabs" role="tablist" aria-label={label}>
        {tabs.map((tb, i) => (
          <button key={tb.id} ref={(el) => { refs.current[tb.id] = el; }} type="button" role="tab" id={`${base}-tab-${tb.id}`}
            aria-selected={tb.id === active} tabIndex={tb.id === active ? 0 : -1} aria-controls={children !== undefined ? panelId : undefined}
            data-tab={tb.id} className="qx-tab" onClick={() => onSelect(tb.id)} onKeyDown={(e) => move(e, i)}>
            {tb.label}{typeof tb.count === 'number' ? <span className="qx-tab__count">{tb.count}</span> : null}
          </button>
        ))}
      </div>
      {children !== undefined ? <div role="tabpanel" id={panelId} aria-labelledby={`${base}-tab-${active}`} tabIndex={0}>{children}</div> : null}
    </>
  );
}

export interface Column<T> { key: string; header: string; render: (row: T) => ReactNode; narrow?: 'hide' | 'show' }
export function DataTable<T>({ columns, rows, rowKey, onRowClick, empty, caption, selectedKey }: {
  columns: readonly Column<T>[]; rows: readonly T[]; rowKey: (row: T) => string; onRowClick?: (row: T) => void; empty: string; caption?: string; selectedKey?: string | null;
}) {
  if (rows.length === 0) return <EmptyState message={empty} />;
  return (
    <div className="qx-table-wrap">
      <table className="qx-table">
        {caption ? <caption className="qx-sr">{caption}</caption> : null}
        <thead>
          <tr>{columns.map((c) => <th key={c.key} scope="col" data-narrow={c.narrow ?? 'show'}>{c.header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} data-row={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} className={[onRowClick ? 'qx-row--link' : '', selectedKey && rowKey(r) === selectedKey ? 'qs-selected' : ''].filter(Boolean).join(' ') || undefined}
              aria-current={selectedKey && rowKey(r) === selectedKey ? "true" : undefined}
              // keyboard + screen reader users open the row too (Enter / Space), not only mouse users
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onRowClick(r); } } : undefined}>
              {columns.map((c) => <td key={c.key} data-label={c.header} data-narrow={c.narrow ?? 'show'}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return <Panel className="qx-empty"><Text tone="muted">{message}</Text></Panel>;
}

/** Maps an API error to a translated message. 404 never confirms the resource exists (IDOR rule). */
export function errorMessage(t: Translate, err: unknown): string {
  const e = errorInfo(err);
  if (e.status === 404 || e.code === 'not_found') return t('portal.kit.notFound');
  if (e.reason) {
    const k = `partners.errors.${e.reason}`;
    const msg = t(k);
    if (msg !== k) return msg;
  }
  if (e.code === 'feature_disabled') return t('portal.kit.featureDisabled');
  if (e.status === 401) return t('portal.kit.signedOut');
  if (e.status === 403) return t('portal.kit.forbidden');
  if (e.code === 'validation_failed' || e.code === 'bad_request') return t('portal.kit.invalid');
  if (e.code === 'conflict' || e.code === 'idempotency_conflict') return t('portal.kit.conflict');
  if (e.status === 402 || e.code === 'payment_required') return t('portal.kit.upgradeRequired');
  return t('portal.kit.error');
}

export function ErrorNote({ error }: { error: unknown }) {
  const { t } = useSurface();
  if (!error) return null;
  const info = errorInfo(error);
  return (
    <div className="qx-alert" role="alert" data-code={info.code} data-status={info.status}>
      <Text>{errorMessage(t, error)}</Text>
      {info.correlationId ? <Text tone="muted">{t('portal.kit.reference', { id: info.correlationId })}</Text> : null}
    </div>
  );
}

export function Loadable<T>({ resource, children }: { resource: Resource<T>; children: (data: T) => ReactNode }) {
  const { t } = useSurface();
  if (resource.status === 'loading') return <div className="qx-loading" aria-busy="true"><Text tone="muted">{t('portal.kit.loading')}</Text></div>;
  if (resource.status === 'error') {
    return (
      <div>
        <ErrorNote error={resource.error} />
        <Button onClick={resource.reload}>{t('portal.kit.retry')}</Button>
      </div>
    );
  }
  return <>{children(resource.data)}</>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | null; children: (id: string, describedBy?: string) => ReactNode }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : null;
  const errorId = error ? `${id}-error` : null;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  let control = children(id, describedBy);
  // hint and error text are announced with the control (aria-describedby), also for callers that ignore the argument
  if (describedBy && isValidElement(control) && (control as ReactElement<Record<string, unknown>>).props['aria-describedby'] === undefined) {
    control = cloneElement(control as ReactElement<Record<string, unknown>>, { 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined });
  }
  return (
    <div className="qx-field">
      <label htmlFor={id} className="qx-field__label">{label}</label>
      {control}
      {hint ? <span className="qx-field__hint" id={hintId!}>{hint}</span> : null}
      {error ? <span className="qx-field__error" id={errorId!} role="alert">{error}</span> : null}
    </div>
  );
}

export const REASON_MIN = 5;
export const reasonValid = (r: string) => r.trim().length >= REASON_MIN && r.trim().length <= 500;

/** Staff/owner actions that must carry a reason: the submit stays disabled until one is given. */
export function ReasonForm({ label, submitLabel, onSubmit, pending, danger, testId }: {
  label: string; submitLabel: string; onSubmit: (reason: string) => void | Promise<unknown>; pending?: boolean; danger?: boolean; testId?: string;
}) {
  const [reason, setReason] = useState('');
  const { t } = useSurface();
  const ok = reasonValid(reason);
  const submit = (e: FormEvent) => { e.preventDefault(); if (ok) void onSubmit(reason.trim()); };
  return (
    <form className="qx-reason" onSubmit={submit} data-testid={testId}>
      <Field label={label} hint={t('portal.kit.reasonHint', { n: REASON_MIN })}>
        {(id) => <textarea id={id} name="reason" required minLength={REASON_MIN} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <Button type="submit" variant={danger ? 'secondary' : 'primary'} disabled={!ok || pending} data-danger={danger ? 'true' : undefined}>{submitLabel}</Button>
    </form>
  );
}

export function Blade({ title, onClose, children, testId, docked }: { title: string; onClose: () => void; children: ReactNode; testId?: string; docked?: boolean }) {
  const { t } = useSurface();
  return (
    <aside className={docked ? 'qx-blade qx-blade--docked' : 'qx-blade'} role="dialog" aria-modal="false" aria-label={title} data-testid={testId}>
      <header className="qx-blade__head">
        <Heading level={2}>{title}</Heading>
        <Button onClick={onClose} aria-label={t('portal.kit.close')}>{t('portal.kit.close')}</Button>
      </header>
      <div className="qx-blade__body">{children}</div>
    </aside>
  );
}

export function Stat({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className="qx-stat" data-testid={testId}>
      <span className="qx-stat__label">{label}</span>
      <span className="qx-stat__value">{value}</span>
    </div>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="qx-grid">{children}</div>;
}

export function Notice({ tone = 'neutral', children, testId }: { tone?: Tone; children: ReactNode; testId?: string }) {
  return <div className="qx-notice" data-tone={tone} role="status" data-testid={testId}>{children}</div>;
}

/** Money is integer minor units + ISO currency (QM-C06-007). Display only; never used to compute a charge. */
const EXP: Record<string, number> = { USD: 2, AED: 2, EUR: 2, GBP: 2, SAR: 2, JPY: 0, KWD: 3, BHD: 3, OMR: 3 };
export function formatMoney(locale: string, minor: number, currency: string): string {
  const exp = EXP[currency.toUpperCase()] ?? 2;
  const tag = locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE';
  return new Intl.NumberFormat(tag, { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: exp, maximumFractionDigits: exp }).format(minor / 10 ** exp);
}
export function Money({ minor, currency }: { minor: number; currency: string }) {
  const { locale } = useSurface();
  return <span className="qx-money" data-currency={currency}>{formatMoney(locale, minor, currency)}</span>;
}

export function formatWhen(locale: string, iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE-u-ca-gregory', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(d);
}
export function When({ iso }: { iso: string | null | undefined }) {
  const { locale } = useSurface();
  return <time dateTime={iso ?? undefined}>{formatWhen(locale, iso)}</time>;
}

export function Checkbox({ label, checked, onChange, disabled, name }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; name?: string }) {
  return (
    <label className="qx-check">
      <input type="checkbox" name={name} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
